import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  FormControlLabel,
  Switch,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Slider,
  Drawer,
  Divider
} from '@mui/material';
import { ShowChart, TrendingUp, TrendingDown, TrendingFlat, PlayArrow, Pause, Settings, Close, OpenInNew } from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import SunCalc from 'suncalc';
import { api } from '../../utils/api';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import moment from 'moment';
import muskyMetricsLogo from '../../assets/musky-metrics-logo.png';
import walleyeInsiderLogo from '../../assets/walleye-insider-logo.png';
import bassScoutLogo from '../../assets/bass-scout-logo.png';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Create custom colored pin icons using circle markers with trend indicator
const createColoredIcon = (color, score, trend) => {
  // Calculate brightness to determine if we need white or black text
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  const textColor = brightness > 128 ? '#000' : '#fff';
  
  // Determine trend arrow
  let trendArrow = '';
  let trendColor = textColor;
  if (trend === 'up') {
    trendArrow = '↗';
    trendColor = '#4caf50'; // Green for up
  } else if (trend === 'down') {
    trendArrow = '↘';
    trendColor = '#f44336'; // Red for down
  } else if (trend === 'flat') {
    trendArrow = '→';
    trendColor = '#ffa726'; // Orange for flat
  }
  
  return new L.DivIcon({
    className: 'custom-pin',
    html: `
      <svg width="45" height="30" viewBox="0 0 45 30" xmlns="http://www.w3.org/2000/svg">
        <circle cx="15" cy="15" r="12" 
                fill="#${color}" 
                stroke="#000" 
                stroke-width="2"/>
        <text x="15" y="19" 
              text-anchor="middle" 
              font-size="10" 
              font-weight="bold" 
              fill="${textColor}">${score}</text>
        ${trendArrow ? `<text x="32" y="20" 
              text-anchor="middle" 
              font-size="20" 
              font-weight="bold" 
              fill="${trendColor}">${trendArrow}</text>` : ''}
      </svg>
    `,
    iconSize: [45, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15],
  });
};

// Create moon icon
const createMoonIcon = (phaseFraction) => {
  // Determine moon phase emoji or use SVG
  const size = 40;
  return new L.DivIcon({
    className: 'moon-icon',
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        background: linear-gradient(90deg, #f4d03f ${phaseFraction * 100}%, #2c3e50 ${phaseFraction * 100}%);
        box-shadow: 0 0 20px rgba(244, 208, 63, 0.8), 0 0 40px rgba(244, 208, 63, 0.4);
        border: 2px solid #f4d03f;
        position: relative;
      ">
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 24px;
        ">🌙</div>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
};

// Create sun icon
const createSunIcon = () => {
  const size = 45;
  return new L.DivIcon({
    className: 'sun-icon',
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        background: radial-gradient(circle, #ffeb3b 40%, #ffc107 100%);
        box-shadow: 0 0 25px rgba(255, 235, 59, 0.9), 0 0 50px rgba(255, 235, 59, 0.5);
        border: 2px solid #ffa000;
        position: relative;
      ">
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 28px;
        ">☀️</div>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
};

// Helper function to interpolate between two RGB colors
const interpolateColor = (color1, color2, factor) => {
  const r1 = parseInt(color1.substring(0, 2), 16);
  const g1 = parseInt(color1.substring(2, 4), 16);
  const b1 = parseInt(color1.substring(4, 6), 16);
  
  const r2 = parseInt(color2.substring(0, 2), 16);
  const g2 = parseInt(color2.substring(2, 4), 16);
  const b2 = parseInt(color2.substring(4, 6), 16);
  
  const r = Math.round(r1 + (r2 - r1) * factor);
  const g = Math.round(g1 + (g2 - g1) * factor);
  const b = Math.round(b1 + (b2 - b1) * factor);
  
  return ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
};

// Function to get icon color based on activity level with gradient
const getActivityColor = (score) => {
  // Handle invalid scores
  if (score === undefined || score === null || isNaN(score)) {
    console.warn('Invalid biometric score:', score);
    return 'CCCCCC'; // Return gray for invalid scores
  }
  
  const darkRed = '8B0000';      // Dark red for 0
  const brightRed = 'FF0000';    // Bright red for 30
  const darkYellow = 'DAA520';   // Dark yellow for 31 and 60 (goldenrod - lighter)
  const brightYellow = 'FFFF00'; // Bright yellow for 45 (pure yellow)
  const brightGreen = '00FF00';  // Bright green for 61
  const darkGreen = '388e3c';    // Dark green for 90
  
  // Clamp score to 0-100 range
  const clampedScore = Math.max(0, Math.min(100, score));
  
  if (clampedScore <= 30) {
    // 0-30: dark red to bright red
    const factor = clampedScore / 30;
    return interpolateColor(darkRed, brightRed, factor);
  } else if (clampedScore < 46) {
    // 31-45: dark yellow to bright yellow
    const factor = (clampedScore - 31) / (45 - 31);
    return interpolateColor(brightYellow, darkYellow, factor);
  } else if (clampedScore < 61) {
    // 46-60: bright yellow to dark yellow
    const factor = (clampedScore - 46) / (61 - 46);
    return interpolateColor(darkYellow, brightYellow, factor);
  } else if (clampedScore <= 90) {
    // 61-90: bright green to dark green
    const factor = (clampedScore - 61) / (90 - 61);
    return interpolateColor(brightGreen, darkGreen, factor);
  } else {
    return darkGreen;
  }
}

function Biometrics() {
  const navigate = useNavigate();
  const location = useLocation();
  const isStandalone = location.pathname === '/biometrics-standalone';
  const [stations, setStations] = useState([]);
  const [rawActivityData, setRawActivityData] = useState({}); // Store raw activity data
  const [weatherStations, setWeatherStations] = useState([]); // Store weather stations
  const [selectedStation, setSelectedStation] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);
  const [selectedApp, setSelectedApp] = useState('Musky Metrics');
  const [timeOffset, setTimeOffset] = useState(0); // Minutes from current time (0 to 2880 = 48 hours)
  const [isPlaying, setIsPlaying] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  // OpenWeatherMap layer toggles
  const [weatherLayers, setWeatherLayers] = useState({
    clouds: false,
    precipitation: false,
    pressure: false,
    wind: false,
    temp: false
  });
  
  // Moon position overlay toggle
  const [showMoonOverlay, setShowMoonOverlay] = useState(false);
  const [moonPositionData, setMoonPositionData] = useState([]);
  
  // Sun position overlay toggle
  const [showSunOverlay, setShowSunOverlay] = useState(false);
  const [sunPositionData, setSunPositionData] = useState([]);
  
  const OWM_API_KEY = '7e1beea95bdd57ee1406540fee381207';

  // Function to determine activity level based on score
  const getActivityLevel = (score) => {
    if (score >= 0 && score <= 30) return { label: 'Sluggish', color: 'error' };
    if (score > 30 && score <= 60) return { label: 'Normal', color: 'warning' };
    if (score > 60 && score <= 90) return { label: 'Aggressive', color: 'success' };
    return { label: 'Unknown', color: 'default' };
  };

  // Calculate moon position - find where moon is at highest altitude (sublunar point)
  const calculateMoonOverlay = useCallback(() => {
    const targetDate = moment().add(timeOffset, 'minutes').toDate();
    
    // For US map visibility, we'll search for the longitude where moon is highest at latitude 40
    const fixedLat = 40;
    let maxAltitude = -90;
    let moonPosition = null;
    
    // Search across longitudes to find where moon is highest at our fixed latitude
    for (let lng = -180; lng <= 180; lng += 2) {
      const moonPos = SunCalc.getMoonPosition(targetDate, fixedLat, lng);
      const altitudeDeg = moonPos.altitude * (180 / Math.PI);
      
      if (altitudeDeg > maxAltitude) {
        maxAltitude = altitudeDeg;
        moonPosition = {
          lat: fixedLat,
          lng,
          altitude: altitudeDeg,
          azimuth: moonPos.azimuth * (180 / Math.PI)
        };
      }
    }
    
    // Refine the longitude position with finer resolution
    if (moonPosition) {
      const refineLng = moonPosition.lng;
      
      for (let lng = refineLng - 2; lng <= refineLng + 2; lng += 0.25) {
        const moonPos = SunCalc.getMoonPosition(targetDate, fixedLat, lng);
        const altitudeDeg = moonPos.altitude * (180 / Math.PI);
        
        if (altitudeDeg > maxAltitude) {
          maxAltitude = altitudeDeg;
          moonPosition = {
            lat: fixedLat,
            lng,
            altitude: altitudeDeg,
            azimuth: moonPos.azimuth * (180 / Math.PI)
          };
        }
      }
    }
    
    // Get moon illumination for icon rendering
    const moonIllum = SunCalc.getMoonIllumination(targetDate);
    if (moonPosition) {
      moonPosition.fraction = moonIllum.fraction;
      moonPosition.phase = moonIllum.phase;
    }
    
    console.log('Moon position calculated:', moonPosition);
    setMoonPositionData(moonPosition ? [moonPosition] : []);
  }, [timeOffset]);

  // Recalculate moon position when time offset changes or moon overlay is enabled
  useEffect(() => {
    if (showMoonOverlay) {
      calculateMoonOverlay();
    }
  }, [showMoonOverlay, calculateMoonOverlay]);

  // Calculate sun position - find where sun is at highest altitude
  const calculateSunOverlay = useCallback(() => {
    const targetDate = moment().add(timeOffset, 'minutes').toDate();
    
    // For US map visibility, we'll search for the longitude where sun is highest at latitude 40
    const fixedLat = 40;
    let maxAltitude = -90;
    let sunPosition = null;
    
    // Search across longitudes to find where sun is highest at our fixed latitude
    for (let lng = -180; lng <= 180; lng += 2) {
      const sunPos = SunCalc.getPosition(targetDate, fixedLat, lng);
      const altitudeDeg = sunPos.altitude * (180 / Math.PI);
      
      if (altitudeDeg > maxAltitude) {
        maxAltitude = altitudeDeg;
        sunPosition = {
          lat: fixedLat,
          lng,
          altitude: altitudeDeg,
          azimuth: sunPos.azimuth * (180 / Math.PI)
        };
      }
    }
    
    // Refine the longitude position with finer resolution
    if (sunPosition) {
      const refineLng = sunPosition.lng;
      
      for (let lng = refineLng - 2; lng <= refineLng + 2; lng += 0.25) {
        const sunPos = SunCalc.getPosition(targetDate, fixedLat, lng);
        const altitudeDeg = sunPos.altitude * (180 / Math.PI);
        
        if (altitudeDeg > maxAltitude) {
          maxAltitude = altitudeDeg;
          sunPosition = {
            lat: fixedLat,
            lng,
            altitude: altitudeDeg,
            azimuth: sunPos.azimuth * (180 / Math.PI)
          };
        }
      }
    }
    
    console.log('Sun position calculated:', sunPosition);
    setSunPositionData(sunPosition ? [sunPosition] : []);
  }, [timeOffset]);

  // Recalculate sun position when time offset changes or sun overlay is enabled
  useEffect(() => {
    if (showSunOverlay) {
      calculateSunOverlay();
    }
  }, [showSunOverlay, calculateSunOverlay]);

  // Fetch weather stations and their biometric data (only on mount)
  const fetchStationsWithBiometrics = async () => {
    try {
      setLoading(true);
      
      // Fetch weather stations from API
      const stationsData = await api.get('/weather-stations');
      const stations = stationsData.stations || [];
      setWeatherStations(stations);

      // Fetch biometric activity data from Firestore
      const activitiesSnapshot = await getDocs(collection(db, 'activities'));
      const activityData = {};
      
      activitiesSnapshot.forEach((doc) => {
        const data = doc.data();
        const activities = data.activities || [];
        
        if (activities.length > 0) {
          // Sort by dateTime descending
          const sortedActivities = [...activities].sort((a, b) => {
            return new Date(b.dateTime) - new Date(a.dateTime);
          });
          
          activityData[doc.id] = sortedActivities;
        }
      });
      
      setRawActivityData(activityData);
    } catch (err) {
      console.error('Error fetching stations:', err);
      setError('Failed to load biometric data');
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate biometric scores based on current timeOffset
  const calculateBiometricScores = () => {
    if (weatherStations.length === 0 || Object.keys(rawActivityData).length === 0) {
      return;
    }
    
    const biometricData = {};
    
    Object.entries(rawActivityData).forEach(([stationId, sortedActivities]) => {
      // Get the most recent score and divide by 2 (as per BiometricActivity logic)
      const selectedTime = moment().add(timeOffset, 'minutes').utc().toISOString();
      const filteredActivities = sortedActivities.filter(a => a.dateTime <= selectedTime);
      // Since sortedActivities is descending (newest first), take the first element [0] after filtering
      const recentScore = filteredActivities.length > 0 ? (filteredActivities[0].score || 0) / 2 : 0;
      
      if (stationId === "RHI") {
        console.log("RHI - Current Activity:", filteredActivities[0], "Recent Score:", recentScore);
      }
      if (recentScore === undefined || isNaN(recentScore)) {
        console.warn(`Station ${stationId} has invalid score:`, recentScore);
      }
      // Calculate trend over next 4 hours from selected time
      const trend = calculateTrend(sortedActivities, recentScore, timeOffset);
      
      biometricData[stationId] = {
        score: recentScore,
        trend: trend
      };
    });

    // Merge station data with biometric scores
    const stationsWithBiometrics = weatherStations.map(station => ({
      ...station,
      biometricScore: biometricData[station.stationId]?.score || 0,
      biometricTrend: biometricData[station.stationId]?.trend || 'unknown',
      activityLevel: getActivityLevel(biometricData[station.stationId]?.score || 0)
    }));

    setStations(stationsWithBiometrics);
  };

  // Calculate trend based on future 4-hour forecast from selected time
  const calculateTrend = (sortedActivities, currentScore, timeOffsetMinutes) => {
    const selectedTime = new Date(Date.now() + timeOffsetMinutes * 60 * 1000);
    const fourHoursFromSelected = new Date(selectedTime.getTime() + 4 * 60 * 60 * 1000);
    
    // Find activities in the next 4 hours from selected time (sorted descending by time)
    const futureActivities = sortedActivities.filter(activity => {
      const activityTime = new Date(activity.dateTime);
      return activityTime > selectedTime && activityTime <= fourHoursFromSelected;
    });
    
    if (futureActivities.length === 0) {
      return 'unknown';
    }
    
    // Get the average score for the next 4 hours
    const futureScores = futureActivities.map(a => (a.score || 0) / 2);
    const avgFutureScore = futureScores.reduce((sum, score) => sum + score, 0) / futureScores.length;
    
    // Calculate the difference
    const diff = avgFutureScore - currentScore;
    
    // Threshold for considering a trend (5 points)
    if (Math.abs(diff) < 5) {
      return 'flat';
    }
    
    return diff > 0 ? 'up' : 'down';
  };

  // Fetch data only on mount
  useEffect(() => {
    fetchStationsWithBiometrics();
  }, []);
  
  // Recalculate scores when timeOffset or raw data changes
  useEffect(() => {
    calculateBiometricScores();
  }, [timeOffset, rawActivityData, weatherStations]);
  
  // Animation loop for play functionality
  useEffect(() => {
    if (!isPlaying) return;
    
    const interval = setInterval(() => {
      setTimeOffset(prev => {
        const next = prev + 15; // Move forward by 15 minutes
        if (next > 2880) {
          return 0; // Loop back to start
        }
        return next;
      });
    }, 250); // Update every 250ms for smooth animation
    
    return () => clearInterval(interval);
  }, [isPlaying]);
  
  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleMarkerClick = (station) => {
    setSelectedStation(station);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedStation(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Filter stations based on showActiveOnly setting
  const filteredStations = stations.filter(station => station.active);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, backgroundColor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: isStandalone ? 'none' : 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h4" gutterBottom>
            Fishing Activity Forecast
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => window.open('/biometrics-standalone', '_blank', 'toolbar=no,menubar=no,location=no,status=no,scrollbars=no')}
            title="Open in new tab"
            size="small"
          >
            <OpenInNew />
          </IconButton>
          <FormControl sx={{ minWidth: 220 }} size="small">
            <Select
              value={selectedApp}
              onChange={(e) => setSelectedApp(e.target.value)}
              displayEmpty
            >
              <MenuItem value="Musky Metrics">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <img src={muskyMetricsLogo} alt="Musky Metrics" style={{ height: 24 }} />
                  <span>Musky Metrics</span>
                </Box>
              </MenuItem>
              <MenuItem value="Walleye Insider">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <img src={walleyeInsiderLogo} alt="Walleye Insider" style={{ height: 24 }} />
                  <span>Walleye Insider</span>
                </Box>
              </MenuItem>
              <MenuItem value="Bass Scout">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <img src={bassScoutLogo} alt="Bass Scout" style={{ height: 24 }} />
                  <span>Bass Scout</span>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          </Box>
        </Box>

        {/* Time Slider */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: 'nowrap', width: 350 }}>
            Forecast Time: {moment().add(timeOffset, 'minutes').format('MMM D, YYYY h:mm A')}
            {timeOffset > 0 && ` (+${Math.floor(timeOffset / 60)}h ${timeOffset % 60}m)`}
          </Typography>
          <Box sx={{ flexGrow: 1, px: 1 }}>
            <Slider
              value={timeOffset}
              onChange={(e, newValue) => setTimeOffset(newValue)}
              min={0}
              max={2880}
              step={15}
              marks={[
                { value: 0, label: 'Now' },
                { value: 720, label: '12h' },
                { value: 1440, label: '24h' },
                { value: 2160, label: '36h' },
                { value: 2880, label: '48h' }
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => {
                const hours = Math.floor(value / 60);
                const mins = value % 60;
                return `+${hours}h ${mins}m`;
              }}
              sx={{ mt: 2 }}
            />
          </Box>
          <IconButton onClick={() => setSettingsOpen(true)} title="Map settings" size="small">
            <Settings />
          </IconButton>
        </Box>
        
      </Box>

      {/* Settings Drawer */}
      <Drawer
        anchor="right"
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: 300, p: 2 } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Map Settings</Typography>
          <IconButton onClick={() => setSettingsOpen(false)} size="small">
            <Close />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* Play/Pause */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <IconButton
            onClick={togglePlayPause}
            color="primary"
            sx={{ backgroundColor: 'action.hover', '&:hover': { backgroundColor: 'action.selected' } }}
          >
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>
          <Typography variant="body2">
            {isPlaying ? 'Pause animation' : 'Play forecast animation'}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {`${filteredStations.length} active stations displayed`}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {/* Legend */}
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>Activity Legend</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#388e3c', borderRadius: '50%', flexShrink: 0 }} />
            <Typography variant="body2">Aggressive (60-90)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#fbc02d', borderRadius: '50%', flexShrink: 0 }} />
            <Typography variant="body2">Normal (30-60)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#800000', borderRadius: '50%', flexShrink: 0 }} />
            <Typography variant="body2">Sluggish (0-30)</Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Weather Layer Controls */}
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>Weather Layers</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <FormControlLabel
            control={<Switch checked={weatherLayers.clouds} onChange={(e) => setWeatherLayers({ ...weatherLayers, clouds: e.target.checked })} color="primary" size="small" />}
            label="Clouds"
          />
          <FormControlLabel
            control={<Switch checked={weatherLayers.precipitation} onChange={(e) => setWeatherLayers({ ...weatherLayers, precipitation: e.target.checked })} color="primary" size="small" />}
            label="Precipitation"
          />
          <FormControlLabel
            control={<Switch checked={weatherLayers.pressure} onChange={(e) => setWeatherLayers({ ...weatherLayers, pressure: e.target.checked })} color="primary" size="small" />}
            label="Pressure"
          />
          <FormControlLabel
            control={<Switch checked={weatherLayers.wind} onChange={(e) => setWeatherLayers({ ...weatherLayers, wind: e.target.checked })} color="primary" size="small" />}
            label="Wind"
          />
          <FormControlLabel
            control={<Switch checked={weatherLayers.temp} onChange={(e) => setWeatherLayers({ ...weatherLayers, temp: e.target.checked })} color="primary" size="small" />}
            label="Temperature"
          />
          <FormControlLabel
            control={<Switch checked={showMoonOverlay} onChange={(e) => setShowMoonOverlay(e.target.checked)} color="secondary" size="small" />}
            label="Moon Position"
          />
          <FormControlLabel
            control={<Switch checked={showSunOverlay} onChange={(e) => setShowSunOverlay(e.target.checked)} color="warning" size="small" />}
            label="Sun Position"
          />
        </Box>
      </Drawer>

      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        <MapContainer
          center={[39.8283, -98.5795]} // Center of USA
          zoom={5}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* OpenWeatherMap Layers */}
          {weatherLayers.clouds && (
            <TileLayer
              attribution='&copy; <a href="https://openweathermap.org">OpenWeatherMap</a>'
              url={`https://tile.openweathermap.org/map/clouds_new/{z}/{x}/{y}.png?appid=${OWM_API_KEY}`}
              opacity={0.8}
            />
          )}
          
          {weatherLayers.precipitation && (
            <TileLayer
              attribution='Weather Radar'
              url="https://mesonet.agron.iastate.edu/cache/tile.py/1.0.0/nexrad-n0q-900913/{z}/{x}/{y}.png"
              opacity={0.6}
            />
          )}
          
          {weatherLayers.pressure && (
            <TileLayer
              attribution='&copy; <a href="https://openweathermap.org">OpenWeatherMap</a>'
              url={`https://tile.openweathermap.org/map/pressure_new/{z}/{x}/{y}.png?appid=${OWM_API_KEY}`}
              opacity={1}
            />
          )}
          
          {weatherLayers.wind && (
            <TileLayer
              attribution='&copy; <a href="https://openweathermap.org">OpenWeatherMap</a>'
              url={`https://tile.openweathermap.org/map/wind_new/{z}/{x}/{y}.png?appid=${OWM_API_KEY}`}
              opacity={0.8}
            />
          )}
          
          {weatherLayers.temp && (
            <TileLayer
              attribution='&copy; <a href="https://openweathermap.org">OpenWeatherMap</a>'
              url={`https://tile.openweathermap.org/map/temp_new/{z}/{x}/{y}.png?appid=${OWM_API_KEY}`}
              opacity={0.8}
            />
          )}
          
          {/* Moon Position Overlay - Single moving moon icon */}
          {showMoonOverlay && moonPositionData.length > 0 && (
            <Marker
              position={[moonPositionData[0].lat, moonPositionData[0].lng]}
              icon={createMoonIcon(moonPositionData[0].fraction)}
              zIndexOffset={1000}
            >
              <Tooltip permanent direction="top" offset={[0, -25]}>
                <div style={{ textAlign: 'center' }}>
                  <strong>Moon Position</strong><br />
                  Alt: {moonPositionData[0].altitude.toFixed(1)}°<br />
                  Illum: {(moonPositionData[0].fraction * 100).toFixed(0)}%
                </div>
              </Tooltip>
            </Marker>
          )}
          {console.log('Rendering moon overlay:', showMoonOverlay, 'Moon data:', moonPositionData)}
          
          {/* Sun Position Overlay - Single moving sun icon */}
          {showSunOverlay && sunPositionData.length > 0 && (
            <Marker
              position={[sunPositionData[0].lat, sunPositionData[0].lng]}
              icon={createSunIcon()}
              zIndexOffset={1001}
            >
              <Tooltip permanent direction="bottom" offset={[0, 25]}>
                <div style={{ textAlign: 'center' }}>
                  <strong>Sun Position</strong><br />
                  Alt: {sunPositionData[0].altitude.toFixed(1)}°<br />
                  Az: {sunPositionData[0].azimuth.toFixed(1)}°
                </div>
              </Tooltip>
            </Marker>
          )}
          {console.log('Rendering sun overlay:', showSunOverlay, 'Sun data:', sunPositionData)}
          
          {filteredStations.map((station) => {
            // Clear activity scores for non-Musky Metrics apps
            // Ensure biometricScore is valid before using it
            const safeScore = (station.biometricScore !== undefined && !isNaN(station.biometricScore)) ? station.biometricScore : 0;
            const displayScore = selectedApp === 'Musky Metrics' ? Math.round(safeScore) : 0;
            const displayTrend = selectedApp === 'Musky Metrics' ? station.biometricTrend : 'unknown';
            const displayColor = selectedApp === 'Musky Metrics' ? getActivityColor(safeScore) : 'CCCCCC';
            
            return (
              <Marker
                key={`${station.stationId}-${displayScore}-${displayTrend}-${timeOffset}`}
                position={[station.latitude, station.longitude]}
                icon={createColoredIcon(displayColor, displayScore, displayTrend)}
                eventHandlers={{
                  click: () => handleMarkerClick(station)
                }}
              />
            );
          })}
        </MapContainer>
      </Box>

      {/* Station Details Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Biometric Activity: {selectedStation?.stationId}
          <IconButton
            onClick={() => navigate(`/biometric-activity/${selectedStation?.stationId}`, { state: { from: '/biometrics' } })}
            sx={{ color: 'primary.main', width: 'auto' }}
            aria-label="View Biometric Activity Chart"
          >
            <ShowChart />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedStation && (
            <>
            <Grid container spacing={2} paddingTop={2}>
              <Grid item xs={12}>
                <Chip
                  label={selectedStation.active ? 'Active' : 'Inactive'}
                  color={selectedStation.active ? 'success' : 'default'}
                  sx={{ mb: 1, mr: 1 }}
                />
                <Chip
                  label={`${selectedStation.activityLevel.label} (${Math.round(selectedStation.biometricScore)})`}
                  color={selectedStation.activityLevel.color}
                  sx={{ mb: 1, mr: 1 }}
                />
                {selectedStation.biometricTrend === 'up' && (
                  <Chip
                    icon={<TrendingUp />}
                    label="Trending Up (4h)"
                    color="success"
                    sx={{ mb: 1 }}
                  />
                )}
                {selectedStation.biometricTrend === 'down' && (
                  <Chip
                    icon={<TrendingDown />}
                    label="Trending Down (4h)"
                    color="error"
                    sx={{ mb: 1 }}
                  />
                )}
                {selectedStation.biometricTrend === 'flat' && (
                  <Chip
                    icon={<TrendingFlat />}
                    label="Stable (4h)"
                    color="warning"
                    sx={{ mb: 1 }}
                  />
                )}
              </Grid>
            </Grid>
            <Grid container spacing={2} paddingTop={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Latitude
                </Typography>
                <Typography variant="body1">
                  {selectedStation.latitude}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Longitude
                </Typography>
                <Typography variant="body1">
                  {selectedStation.longitude}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} paddingTop={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Biometric Score
                </Typography>
                <Typography variant="h5" color={selectedStation.activityLevel.color}>
                  {Math.round(selectedStation.biometricScore)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedStation.activityLevel.label} activity level
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} paddingTop={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Primary BUFKIT URL
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                  {selectedStation.bufkitUrl1 || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Secondary BUFKIT URL
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                  {selectedStation.bufkitUrl2 || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} paddingTop={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Last Event Date
                </Typography>
                <Typography variant="body1">
                  {selectedStation.lastEventDate || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Last Forecast
                </Typography>
                <Typography variant="body1">
                  {selectedStation.lastForecastDatetime ? 
                    selectedStation.lastForecastDatetime.replace('T', ' ').replace(':00+00:00', ' EST') : 
                    'N/A'
                  }
                </Typography>
              </Grid>
            </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Close
          </Button>
          <Button
            onClick={() => navigate(`/biometric-activity/${selectedStation?.stationId}`, { state: { from: '/biometrics' } })}
            variant="contained"
            color="primary"
            startIcon={<ShowChart />}
          >
            View Details
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Biometrics;
